import { Link } from "react-router-dom";
import { SUPPORT_EMAIL } from "@/components/PublicLayout";

const FEATURES = [
  {
    title: "Conversational memory",
    body:
      "Journey remembers the people, projects, and preferences that matter so the owner doesn't repeat themselves across sessions.",
  },
  {
    title: "Reminders that show up",
    body:
      "Web push and on-device notifications anchored to the owner's timezone — set from natural conversation.",
  },
  {
    title: "Delegated follow-ups",
    body:
      "With the owner's explicit permission, Journey can text named contacts on the owner's behalf and route their replies back.",
  },
];

export default function Home() {
  return (
    <div className="max-w-5xl mx-auto px-5 sm:px-8">
      {/* Hero */}
      <section className="pt-16 sm:pt-24 pb-20">
        <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full border border-cyan-400/25 bg-cyan-500/10 text-cyan-200 text-xs mb-6">
          <span className="w-1.5 h-1.5 rounded-full bg-cyan-300 animate-pulse" />
          Private AI assistant
        </div>
        <h1
          className="font-display text-4xl sm:text-5xl lg:text-6xl leading-tight tracking-tight max-w-3xl"
          data-testid="public-home-heading"
        >
          A thoughtful AI companion that <span className="text-cyan-300">remembers</span>, plans, and follows up.
        </h1>
        <p className="mt-6 text-white/65 text-base sm:text-lg max-w-2xl leading-relaxed">
          Journey is a private, single-owner assistant. It holds context across
          conversations, surfaces reliable reminders, and — with explicit
          permission — handles the small follow-ups its owner would rather not
          chase manually. This site exists for informational, privacy, and
          messaging-compliance purposes; the assistant itself is not publicly
          accessible.
        </p>

        <div className="mt-9 flex flex-wrap items-center gap-3">
          <a
            href={`mailto:${SUPPORT_EMAIL}`}
            data-testid="public-home-contact"
            className="inline-flex items-center gap-2 rounded-full bg-cyan-400 hover:bg-cyan-300 text-[#04141a] px-5 py-2.5 text-sm font-medium transition-colors"
          >
            Contact support
          </a>
          <Link
            to="/messaging"
            data-testid="public-home-messaging-link"
            className="inline-flex items-center gap-2 rounded-full border border-white/15 hover:border-white/30 text-white/80 hover:text-white px-5 py-2.5 text-sm transition-colors"
          >
            How Journey uses SMS
          </Link>
        </div>
      </section>

      {/* Features */}
      <section className="grid sm:grid-cols-3 gap-4 sm:gap-5 pb-20">
        {FEATURES.map((f) => (
          <div
            key={f.title}
            className="glass rounded-2xl p-6 hover:border-white/10 transition-colors"
            data-testid={`public-home-feature-${f.title.toLowerCase().split(" ")[0]}`}
          >
            <h3 className="font-display text-lg mb-2">{f.title}</h3>
            <p className="text-white/60 text-sm leading-relaxed">{f.body}</p>
          </div>
        ))}
      </section>

      {/* SMS / messaging disclosure — important for Twilio verification */}
      <section className="pb-20">
        <div className="glass-strong rounded-3xl p-8 sm:p-10 border border-white/5">
          <div className="text-xs uppercase tracking-wider text-cyan-300/80 mb-3">
            Messaging usage
          </div>
          <h2 className="font-display text-2xl sm:text-3xl mb-4 max-w-2xl">
            How Journey uses SMS
          </h2>
          <p className="text-white/65 leading-relaxed max-w-3xl">
            Journey sends and receives SMS only on behalf of the account owner, and only for
            delegated task follow-ups the owner explicitly creates. Recipients always receive
            context about who initiated the message, can reply <code className="text-cyan-200">STOP</code> to
            opt out at any time, and can email{" "}
            <a href={`mailto:${SUPPORT_EMAIL}`} className="text-cyan-300 hover:text-cyan-200">
              {SUPPORT_EMAIL}
            </a>{" "}
            for support or data removal. Messaging is transactional and operational — not marketing,
            promotional, or bulk.
          </p>
          <div className="mt-6 flex flex-wrap gap-3 text-sm">
            <Link
              to="/privacy"
              className="rounded-full border border-white/15 hover:border-white/30 px-4 py-1.5 text-white/80 hover:text-white transition-colors"
              data-testid="public-home-privacy-link"
            >
              Read Privacy Policy
            </Link>
            <Link
              to="/terms"
              className="rounded-full border border-white/15 hover:border-white/30 px-4 py-1.5 text-white/80 hover:text-white transition-colors"
              data-testid="public-home-terms-link"
            >
              Read Terms
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
}
